import { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { FiHome, FiChevronDown, FiCheck, FiPlus } from 'react-icons/fi';

import { apiJson } from '@/lib/api';
import { track } from '@/lib/analytics';
import {
  householdLabel,
  useOptionalHouseholds,
} from '@/lib/household-context';

/**
 * Low-key household switcher for the web app. Renders nothing for
 * single-household users; multi-household users get a small dropdown to switch
 * households or add a new one.
 */
export function HouseholdSwitcher({
  currentUserId,
}: {
  currentUserId: number | null;
}) {
  const household = useOptionalHouseholds();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  if (!household || household.householdCount < 2) {
    return null;
  }

  const {
    households,
    currentHousehold,
    currentHouseholdId,
    setCurrentHouseholdId,
  } = household;

  const ordered = [...households].sort((a, b) => a.id - b.id);
  const currentIndex = ordered.findIndex((h) => h.id === currentHouseholdId);
  const currentLabel =
    currentHousehold && currentIndex >= 0
      ? householdLabel(currentHousehold, currentIndex, currentUserId)
      : 'Household';

  const handleAddHousehold = async () => {
    track('Add Household Tapped', { household_count: households.length });
    setCreating(true);
    try {
      const created = await apiJson<{ id: number }>(getToken, '/v1/households', {
        method: 'POST',
      });
      await queryClient.invalidateQueries({ queryKey: ['households'] });
      setCurrentHouseholdId(created.id, { track: false });
      track('Add Household Completed', { active_household_id: created.id });
      setOpen(false);
      navigate({ to: '/app/onboarding/children' });
    } catch {
      // best-effort; keep the menu open to retry
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className='relative px-4 pt-4 lg:px-6'>
      <button
        type='button'
        onClick={() => {
          if (!open) {
            track('Household Switcher Opened', {
              household_count: households.length,
              active_household_id: currentHouseholdId,
            });
          }
          setOpen((v) => !v);
        }}
        className='inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-primary shadow-sm hover:bg-gray-50'
      >
        <FiHome className='h-4 w-4' />
        <span className='max-w-[200px] truncate'>{currentLabel}</span>
        <FiChevronDown className='h-4 w-4' />
      </button>

      {open && (
        <>
          <div
            className='fixed inset-0 z-40'
            onClick={() => setOpen(false)}
          />
          <div className='absolute left-4 z-50 mt-2 w-72 rounded-xl border border-gray-200 bg-white py-2 shadow-lg lg:left-6'>
            {ordered.map((h, index) => {
              const selected = h.id === currentHouseholdId;
              return (
                <button
                  key={h.id}
                  type='button'
                  onClick={() => {
                    if (h.id !== currentHouseholdId) {
                      setCurrentHouseholdId(h.id);
                    }
                    setOpen(false);
                  }}
                  className='flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left text-sm hover:bg-gray-50'
                >
                  <span className='truncate'>
                    {householdLabel(h, index, currentUserId)}
                  </span>
                  {selected && (
                    <FiCheck className='h-4 w-4 flex-shrink-0 text-primary' />
                  )}
                </button>
              );
            })}
            <div className='my-1 border-t border-gray-100' />
            <button
              type='button'
              onClick={handleAddHousehold}
              disabled={creating}
              className='flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-semibold text-primary hover:bg-gray-50 disabled:opacity-60'
            >
              <FiPlus className='h-4 w-4' />
              {creating ? 'Creating…' : 'Add another household'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
